import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, BadgeDollarSign } from 'lucide-react';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RoleGate } from '@/auth/role-gate';
import { Unpriced } from '@/components/allocation/unpriced';
import { isPriced } from '@/lib/allocation/price';
import { useCosts, useIssueAllowance } from './use-costs';

// Recharts is a ~150 KB gzipped dependency — lazy-load it so the case-detail
// route's initial bundle stays under the TDS §11.1 ≤350 KB budget. The
// CostDonut module imports Recharts directly; this lazy() boundary keeps it
// out of the case-detail entry chunk.
const CostDonut = lazy(() =>
  import('./cost-donut').then((m) => ({ default: m.CostDonut })),
);

// Prices tab per docs/ui/operator-ui-tds.md §4.2 + §8 (L2) — what the airline pays for
// the case's hotels. Every amount is the airline's PRICE (operator-bff converts Nexa's
// cost before it leaves the server, commercial-contract §6.13); without a price card the
// BFF sends null and this tab says «No disponible» — never 0, never the cost.
//
// Per-hotel breakdown table, booked-vs-held visualization, and the passenger
// allowance affordance (an allowance, not a price — unchanged) gated by
// <RoleGate min={Role.OPS_SUPERVISOR}>. Recharts donut chart lazy-loaded per TDS
// §11.1's 150 KB Reports+charts budget.

interface CostsTabProps {
  caseUrn: string;
}

export function CostsTab({ caseUrn }: CostsTabProps) {
  const { t } = useTranslation('caseDetail');
  const costs = useCosts(caseUrn);
  const [issueOpen, setIssueOpen] = useState(false);

  if (costs.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full max-w-3xl" />
        <Skeleton className="h-32 w-full max-w-3xl" />
      </div>
    );
  }

  if (costs.isError || !costs.data) {
    return (
      <Card className="max-w-3xl">
        <CardContent className="p-6 text-sm text-destructive">{t('costs.loadError')}</CardContent>
      </Card>
    );
  }

  const d = costs.data;
  const bookingDown = d.sources?.booking === 'unavailable';
  const unpriced = d.priced === false;
  const usd = (n: number | null | undefined) => (typeof n === 'number' ? `$${n.toLocaleString()}` : '—');
  /** A price: the amount, «No disponible» when the BFF could not price it, else '—'. */
  const price = (n: number | null | undefined) => (isPriced(n) ? usd(n) : unpriced ? <Unpriced /> : '—');

  return (
    <div className="flex flex-col gap-4">
      <Card className="max-w-3xl">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>{t('costs.summaryTitle')}</CardTitle>
          <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
            <Button size="sm" className="gap-2" onClick={() => setIssueOpen(true)}>
              <Banknote className="h-3 w-3" aria-hidden />
              {t('costs.issueAllowance')}
            </Button>
          </RoleGate>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-6">
          {isPriced(d.totalContractUSD) && isPriced(d.totalHeldUSD) ? (
            <Suspense fallback={<Skeleton className="h-48 w-48 rounded-full" />}>
              <CostDonut bookedUSD={d.totalContractUSD} heldUSD={d.totalHeldUSD} />
            </Suspense>
          ) : (
            <div className="flex h-48 w-48 items-center justify-center text-sm" data-testid="costs-donut-unpriced">
              {unpriced ? <Unpriced /> : t('costs.donut.empty')}
            </div>
          )}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase text-muted-foreground">{t('costs.committed')}</dt>
              <dd className="text-lg font-semibold" data-testid="costs-booked">
                {bookingDown ? '—' : price(d.totalContractUSD)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">{t('costs.held')}</dt>
              <dd className="text-lg font-semibold" data-testid="costs-held">
                {bookingDown ? '—' : price(d.totalHeldUSD)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">{t('costs.marketReference')}</dt>
              <dd className="text-lg font-semibold text-muted-foreground">
                {usd(d.totalMarketUSD)}
                {d.totalMarketUSD === null && (
                  <span className="ml-1 text-xs font-normal">{t('costs.noMarketSource')}</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">{t('costs.allowanceIssued')}</dt>
              <dd className="text-lg font-semibold text-muted-foreground">{usd(d.allowanceUSD)}</dd>
            </div>
          </dl>
          {bookingDown && (
            <p className="w-full text-xs text-destructive" data-testid="costs-booking-unavailable">
              {t('costs.bookingUnreachable')}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="text-sm">{t('costs.perHotelTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="density-table w-full text-sm">
            <thead className="border-b border-border text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">{t('costs.table.hotel')}</th>
                <th className="px-3 py-2 text-right">{t('costs.table.reservations')}</th>
                <th className="px-3 py-2 text-right">{t('costs.table.rooms')}</th>
                <th className="px-3 py-2 text-right">{t('costs.table.ratePerRoom')}</th>
                <th className="px-3 py-2 text-right">{t('costs.table.booked')}</th>
                <th className="px-3 py-2 text-right">{t('costs.table.held')}</th>
              </tr>
            </thead>
            <tbody>
              {d.lines.length === 0 && (
                <tr>
                  <td className="px-3 py-3 text-muted-foreground" colSpan={6}>
                    {t('costs.noReservations')}
                  </td>
                </tr>
              )}
              {d.lines.map((line) => (
                <tr key={line.hotelUrn} className="border-b border-border last:border-b-0">
                  <td className="px-3 py-2">{line.hotelName}</td>
                  <td className="px-3 py-2 text-right">{line.reservations}</td>
                  <td className="px-3 py-2 text-right">{line.rooms ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{price(line.contractRateUSD)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{price(line.bookedUSD)}</td>
                  <td className="px-3 py-2 text-right">{price(line.heldUSD)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <IssueAllowanceDialog
        caseUrn={caseUrn}
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
      />
    </div>
  );
}

interface IssueDialogProps {
  caseUrn: string;
  open: boolean;
  onClose: () => void;
}

function IssueAllowanceDialog({ caseUrn, open, onClose }: IssueDialogProps) {
  const { t } = useTranslation('caseDetail');
  const issue = useIssueAllowance(caseUrn);
  const [amount, setAmount] = useState(50);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeDollarSign className="h-4 w-4" aria-hidden />
            {t('costs.dialog.title')}
          </DialogTitle>
          <DialogDescription>{t('costs.dialog.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3 text-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            await issue.mutateAsync({ amountUSD: amount, reason });
            onClose();
          }}
        >
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">{t('costs.dialog.amount')}</span>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">{t('costs.dialog.reason')}</span>
            <textarea
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              placeholder={t('costs.dialog.reasonPlaceholder')}
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:common.cancel')}
            </Button>
            <Button type="submit" disabled={issue.isPending || !reason}>
              {issue.isPending ? t('costs.dialog.submitting') : t('costs.dialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
